import { useCallback, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { fetchPaymentRequestList } from '#/api/fmsMgmt/paymentRequest';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { DEFAULT_LIST_PAGE_SIZE } from '#/utils/listUrl/listPaginationDefaults';
import { paymentRequestListQueryKey } from '#/utils/queryKeys';

const EMPTY_PAGE = {
  current_page: 1,
  page_size: DEFAULT_LIST_PAGE_SIZE,
  total_count: 0,
  total_page: 1,
};

const DEFAULT_LIST_QUERY = {
  page: 1,
  pageSize: DEFAULT_LIST_PAGE_SIZE,
  keyword: '',
  sort: [{ id: 'updated_at', desc: true }],
  filters: {},
};

/**
 * 請款列表：本地 listQuery（避免與 hub ?panel= 衝突），server-side 分頁／篩／排序。
 * scope：個人中心傳 self（`/self`，`.self`）；財務管理用預設 all（原路由，`.read`）。
 */
function usePaymentRequestList({ scope = FMS_READ_SCOPE.ALL } = {}) {
  const [listQuery, setListQuery] = useState(DEFAULT_LIST_QUERY);

  const query = useQuery({
    queryKey: paymentRequestListQueryKey(listQuery, scope),
    queryFn: () => fetchPaymentRequestList(listQuery, { scope }),
    placeholderData: keepPreviousData,
  });

  const setPage = useCallback((page) => {
    setListQuery((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize) => {
    setListQuery((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const setKeyword = useCallback((keyword) => {
    setListQuery((prev) => ({ ...prev, keyword, page: 1 }));
  }, []);

  const setFilter = useCallback((key, value) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, []);

  const setSort = useCallback((sort) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: sort ?? [],
    }));
  }, []);

  const removeSortAt = useCallback((index) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: (prev.sort ?? []).filter((_, sortIndex) => sortIndex !== index),
    }));
  }, []);

  const resetListQuery = useCallback(() => {
    setListQuery((prev) => ({
      ...DEFAULT_LIST_QUERY,
      pageSize: prev.pageSize,
    }));
  }, []);

  return {
    rows: query.data?.data ?? [],
    pagination: query.data?.page ?? EMPTY_PAGE,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    refetch: query.refetch,
  };
}

export default usePaymentRequestList;
